import { Button, Empty, Table, TableProps } from 'antd';
import { useEffect, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { onDeleteRole, onGetRoles } from 'modules/roles/services/roles';

// models
import { Role } from 'modules/roles/models/roles';

// components
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import Icon from 'utils/icon';
import AuthActionComponent from 'components/authAction/authAction';
import NoData from 'components/noData/noData';

function ListRolesPage() {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [roles, setRoles] = useState<Role[]>([]);
	const [tempDeletedRole, setTempDeletedRole] = useState<number>();

	// pagination states
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [isLoadMore, setIsLoadMore] = useState(false);

	const columns: TableProps<Role>['columns'] = [
		{
			title: t('ROLES.ROLE_NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name) => <p className="mb-0 fw-medium">{name}</p>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '',
			key: '',
			render: (_, role) =>
				role.name !== 'super_admin' && (
					<div className="d-flex gap-2">
						<AuthActionComponent action="role_update">
							<Link to={`${role.id}`}>
								<Icon name="edit" width={35} height={35} />
							</Link>
						</AuthActionComponent>
						<AuthActionComponent action="role_delete">
							<Button type="link" className="p-0" onClick={() => onShowConfirmation(role.id)}>
								<Icon name="delete" width={35} height={35} />
							</Button>
						</AuthActionComponent>
					</div>
				),
		},
	];

	useEffect(() => {
		getRoles();
	}, []);

	const getRoles = async () => {
		try {
			setIsLoading(true);

			const response = await onGetRoles();
			setRoles(response.data.data.roles);
			setTotalRecords(response.data.data.pagination.total);

			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoadMore(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });
			const response = await onGetRoles(page);
			setRoles(response.data.data.roles);
			setCurrentPage(page);
			setIsLoadMore(false);
		} catch (error) {
			setIsLoadMore(false);
			NotificationService.error(t('COMMON.OPS'), t('COMMON.ERROR_OCCURRED'));
		}
	};

	const onShowConfirmation = (roleId: number) => {
		setShowConfirmation(true);
		setTempDeletedRole(roleId);
	};

	const onDiscardConfirmation = () => {
		setShowConfirmation(false);
	};

	const onDelete = async () => {
		try {
			setIsDeleting(true);
			await onDeleteRole(tempDeletedRole as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ROLES.DELETED'));
			setShowConfirmation(false);
			setIsDeleting(false);
			getRoles();
		} catch (error) {
			setIsDeleting(false);
			setShowConfirmation(false);
			NotificationService.error(t('COMMON.ERROR'), t('ROLES.DELETE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<h3>{t('ROLES.TITLE')}</h3>
			</div>

			{isLoading ? (
				<LoadingComponent />
			) : (
				<div className={`sectionContainer listData`}>
					<header>
						<div>
							<h5>
								{totalRecords} {t('ROLES.ROLES')}
							</h5>
							<p>{t('ROLES.MANAGE_ROLES')}</p>
						</div>
						<AuthActionComponent action="role_create">
							<Link to="create">
								<Button type="primary" icon={<PlusOutlined />} style={{ background: '#F1F1F2', color: '#7E8299', border: 'none' }}>
									{t('COMMON.ADD')}
								</Button>
							</Link>
						</AuthActionComponent>
					</header>

					<div style={{ position: 'relative' }}>
						<Table
							columns={columns}
							dataSource={roles}
							scroll={{ x: 600 }}
							rowKey="id"
							pagination={{
								pageSize: 15,
								total: totalRecords,
								showSizeChanger: false,
								current: currentPage,
								onChange: fetchMoreData,
							}}
							locale={{ emptyText: <NoData /> }}
						/>
						{isLoadMore && (
							<div className="loadMoreContainer">
								<LoadingComponent />
							</div>
						)}
					</div>
				</div>
			)}

			<ConfirmModalComponent
				open={showConfirmation}
				confirmText={t('COMMON.DELETE')}
				onDiscard={onDiscardConfirmation}
				title={t('ROLES.DELETE_ROLE')}
				description={t('ROLES.DELETE_MODAL_DESCRIPTION')}
				note={t('ROLES.DELETE_MODAL_NOTE')}
				onConfirm={onDelete}
				isLoading={isDeleting}
			/>
		</>
	);
}

export default ListRolesPage;
